import './NotificationSettings.scss';

import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { COP_COMMON_ERROR_MESSAGES } from '../../constants/common-error-messages';
import {
  COP_API_BASE_URL,
  getUserNotificationSettingsUrl,
  USER_NOTIFICATION_SETTINGS_ENDPOINT,
} from '../../constants/service-endpoints';
import { ToastService } from '../../services/toast.service';
import { buildCopHeaders, getSilentToken } from '../../services/tokenUtils';
import { BackArrowIcon, EmailIcon, GearIcon, MonitorIcon } from './NotificationSettingsIcons';
import Tabs, { type Tab } from './Tabs';
type SettingsCategory = 'All' | 'Cases' | 'Agreements' | 'Assets';
type DeliverySettings = { inApp: boolean; hideCounter: boolean; email: boolean };
type ChannelKey = keyof DeliverySettings;

type ApiNotificationSetting = {
  id: number;
  category: string;
  emailEnabled?: boolean;
  receiveEmails?: string;
  browserEnabled?: boolean;
  browserMuted?: boolean;
};

type CategoryBrowserSetting = {
  id: number;
  category: string;
  emailEnabled: boolean;
  browserEnabled: boolean;
  browserMuted: boolean;
};

type ApiNotificationSettingsResponse = { settings?: ApiNotificationSetting[] };
type ApiNotificationSettingUpdateName = 'BrowserEnabled' | 'BrowserMuted' | 'EmailEnabled';
type ApiNotificationSettingUpdatePayload = {
  category: SettingsCategory;
  customerId: string;
  name: ApiNotificationSettingUpdateName;
  value: boolean;
};

type NotificationDataCategory = {
  categoryKey?: string;
  sectionTitle?: string;
  sectionDescription?: string;
  showEmailToggle?: boolean;
  inAppLabel?: string;
  inAppDescription?: string;
  hideCounterLabel?: string;
  hideCounterDescription?: string;
  emailLabel?: string;
  emailDescription?: string;
};
type NotificationDataSettings = {
  title?: string;
  categories?: NotificationDataCategory[];
};
type NotificationDataCategoryLabels = { all?: string; cases?: string; agreements?: string; assets?: string };

type NotificationStatusMessageData = { title?: string; description?: string };

type NotificationSettingsProps = {
  onBack: () => void;
  settingsConfig?: NotificationDataSettings;
  categoryLabels?: NotificationDataCategoryLabels;
  backButtonLabel?: string;
  loadErrorMessage?: NotificationStatusMessageData;
  updateErrorMessage?: NotificationStatusMessageData;
  setShowHideSetting: (value: CategoryBrowserSetting[]) => void;
};

const CATEGORY_FROM_KEY: Record<string, SettingsCategory> = { all: 'All', cases: 'Cases', agreements: 'Agreements', assets: 'Assets' };
const CATEGORY_UPDATE_ORDER: SettingsCategory[] = ['All', 'Cases', 'Agreements', 'Assets'];
const CATEGORY_TAB_ID: Record<SettingsCategory, number> = { All: 1, Cases: 2, Agreements: 3, Assets: 4 };
const TAB_ID_CATEGORY_MAP: Record<number, SettingsCategory> = { 1: 'All', 2: 'Cases', 3: 'Agreements', 4: 'Assets' };

const CHANNEL_TO_SETTING_NAME: Record<ChannelKey, ApiNotificationSettingUpdateName> = { inApp: 'BrowserEnabled', hideCounter: 'BrowserMuted', email: 'EmailEnabled' };
const CHANNEL_CONFIG: Record<ChannelKey, { label: string; desc: string; icon: 'monitor' | 'email'; bold?: boolean }> = {
  inApp: { label: 'In-app', desc: 'Turn on/off all portal notifications', icon: 'monitor' },
  hideCounter: { label: 'Hide notification counter', desc: 'Hide the red counter, but still get notifications', icon: 'monitor', bold: true },
  email: { label: 'Email', desc: 'Receive notifications in your mailbox', icon: 'email' },
};

const DEFAULT_CATEGORY_CHANNELS: Record<SettingsCategory, ChannelKey[]> = {
  All: ['inApp', 'hideCounter', 'email'], Cases: ['inApp', 'hideCounter'],
  Agreements: ['inApp', 'hideCounter', 'email'], Assets: ['inApp', 'hideCounter', 'email'],
};
const DEFAULT_INITIAL_SETTINGS: Record<SettingsCategory, DeliverySettings> = {
  All: { inApp: true, hideCounter: false, email: true }, Cases: { inApp: true, hideCounter: false, email: true },
  Agreements: { inApp: true, hideCounter: false, email: true }, Assets: { inApp: true, hideCounter: false, email: false },
};
const mapEmailEnabled = (setting: ApiNotificationSetting, fallback: boolean): boolean => {
  if (typeof setting.emailEnabled === 'boolean') return setting.emailEnabled;
  if (typeof setting.receiveEmails === 'string') return setting.receiveEmails.toLowerCase() === 'true';
  return fallback;
};

const isSettingsCategory = (value: string): value is SettingsCategory =>
  value === 'All' || value === 'Cases' || value === 'Agreements' || value === 'Assets';
const getCustomerIdFromHeaders = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== 'object') return undefined;
  const typedHeaders = headers as Record<string, unknown>;
  const value = typedHeaders['X-Customer-Id'] ?? typedHeaders['x-customer-id'];
  return typeof value === 'string' && value.trim() ? value : undefined;
};

type ToggleProps = { checked: boolean; onChange: () => void; label: string };
const Toggle = ({ checked, onChange, label }: ToggleProps) => (
  <button role="switch" aria-checked={checked} aria-label={label} className={`ns-toggle ${checked ? 'ns-toggle--on' : ''}`} onClick={onChange} type="button">
    <span className="ns-toggle__thumb" />
  </button>
);
const NotificationSettings = ({ onBack, settingsConfig, categoryLabels, backButtonLabel, loadErrorMessage, updateErrorMessage, setShowHideSetting }: NotificationSettingsProps) => {
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>('All');
  const [apiCategories, setApiCategories] = useState<SettingsCategory[]>([]);

  const settingsCategoryMap = useMemo(() => {
    const map: Partial<Record<SettingsCategory, NotificationDataCategory>> = {};
    if (!Array.isArray(settingsConfig?.categories)) return map;
    settingsConfig.categories.forEach(c => {
      const key = c.categoryKey?.toLowerCase();
      const category = key ? CATEGORY_FROM_KEY[key] : undefined;
      if (category) map[category] = c;
    });
    return map;
  }, [settingsConfig]);

  const categoryChannels = useMemo(() => {
    const next = { ...DEFAULT_CATEGORY_CHANNELS };
    (Object.keys(next) as SettingsCategory[]).forEach(category => {
      if (settingsCategoryMap[category]?.showEmailToggle === false) next[category] = ['inApp', 'hideCounter'];
    });
    return next;
  }, [settingsCategoryMap]);

  const initialSettings = useMemo(() => {
    const next = { ...DEFAULT_INITIAL_SETTINGS };
    (Object.keys(next) as SettingsCategory[]).forEach(category => {
      if (!categoryChannels[category].includes('email')) next[category] = { ...next[category], email: false };
    });
    return next;
  }, [categoryChannels]);

  const sectionDesc = useMemo<Record<SettingsCategory, string>>(() => {
    const next: Record<SettingsCategory, string> = {
      All: '',
      Cases: '',
      Agreements: '',
      Assets: '',
    };
    (Object.keys(next) as SettingsCategory[]).forEach(category => {
      const value = settingsCategoryMap[category]?.sectionDescription?.trim();
      next[category] = value ?? '';
    });
    return next;
  }, [settingsCategoryMap]);

  const tabLabels = useMemo(() => ({
    All: categoryLabels?.all ?? 'All',
    Cases: categoryLabels?.cases ?? 'Cases',
    Agreements: categoryLabels?.agreements ?? 'Agreements',
    Assets: categoryLabels?.assets ?? 'Assets',
  }), [categoryLabels]);

  const panelTitle = settingsConfig?.title?.trim() || 'Notification Settings';
  const sectionTitle = settingsCategoryMap[activeCategory]?.sectionTitle?.trim() || 'Delivery Channel';
  const sectionDescription = sectionDesc[activeCategory] || 'Manage notification delivery settings.';

  const channelConfig = useMemo<Record<ChannelKey, { label: string; desc: string; icon: 'monitor' | 'email'; bold?: boolean }>>(() => {
    const cat = settingsCategoryMap[activeCategory];
    return {
      inApp: { ...CHANNEL_CONFIG.inApp, label: cat?.inAppLabel?.trim() || CHANNEL_CONFIG.inApp.label, desc: cat?.inAppDescription?.trim() || CHANNEL_CONFIG.inApp.desc },
      hideCounter: { ...CHANNEL_CONFIG.hideCounter, label: cat?.hideCounterLabel?.trim() || CHANNEL_CONFIG.hideCounter.label, desc: cat?.hideCounterDescription?.trim() || CHANNEL_CONFIG.hideCounter.desc },
      email: { ...CHANNEL_CONFIG.email, label: cat?.emailLabel?.trim() || CHANNEL_CONFIG.email.label, desc: cat?.emailDescription?.trim() || CHANNEL_CONFIG.email.desc },
    };
  }, [settingsCategoryMap, activeCategory]);

  const [allSettings, setAllSettings] = useState<Record<SettingsCategory, DeliverySettings>>({ ...initialSettings });

  const saveUserNotificationSetting = async (category: SettingsCategory, key: ChannelKey, value: boolean) => {
    try {
      const token = getSilentToken();
      const headers = buildCopHeaders(token);
      const customerId = getCustomerIdFromHeaders(headers);
      if (!customerId) return;
      const payload: ApiNotificationSettingUpdatePayload = { category, customerId, name: CHANNEL_TO_SETTING_NAME[key], value };
      await axios.put(`${COP_API_BASE_URL}${USER_NOTIFICATION_SETTINGS_ENDPOINT}`, payload, { headers });
    } catch (error) {
      const fallbackMessage = updateErrorMessage?.description?.trim() || COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
        '{{action}}',
        COP_COMMON_ERROR_MESSAGES['api-error'].action['notification-settings-saving'],
      )
      const message = error instanceof Error && error.message ? error.message : fallbackMessage
      ToastService.error(message, updateErrorMessage?.title);
    }
  };

  const buildShowHideSettings = (settings: Record<SettingsCategory, DeliverySettings>): CategoryBrowserSetting[] =>
    CATEGORY_UPDATE_ORDER.map((cat, idx) => ({
      id: idx,
      category: cat,
      emailEnabled: settings[cat].email,
      browserEnabled: settings[cat].inApp,
      browserMuted: settings[cat].hideCounter,
    }));

  useEffect(() => {
    const loadUserNotificationSettings = async () => {
      try {
        const token = getSilentToken();
        const headers = buildCopHeaders(token);
        const customerId = getCustomerIdFromHeaders(headers);
        if (!customerId) return;

        const response = await axios.get<ApiNotificationSettingsResponse>(`${COP_API_BASE_URL}${getUserNotificationSettingsUrl(customerId)}`, { headers });
        const apiSettings = response.data?.settings;
        if (!Array.isArray(apiSettings) || !apiSettings.length) return;

        const presentCategories = CATEGORY_UPDATE_ORDER.filter(category =>
          apiSettings.some(setting => setting.category === category),
        );
        if (presentCategories.length) setApiCategories(presentCategories);

        setAllSettings(prev => {
          const next = { ...prev };
          for (const setting of apiSettings) {
            if (!isSettingsCategory(setting.category)) continue;
            const category = setting.category;
            const current = next[category];
            next[category] = {
              ...current,
              inApp: setting.browserEnabled ?? current.inApp,
              hideCounter: setting.browserMuted ?? current.hideCounter,
              email: mapEmailEnabled(setting, current.email),
            };
          }
          setShowHideSetting(buildShowHideSettings(next as Record<SettingsCategory, DeliverySettings>));
          return next;
        });
      } catch (error) {
        const fallbackMessage = loadErrorMessage?.description?.trim() || COP_COMMON_ERROR_MESSAGES['api-error'].message.replace(
          '{{action}}',
          COP_COMMON_ERROR_MESSAGES['api-error'].action['notification-settings-loading'],
        )
        const message = error instanceof Error && error.message ? error.message : fallbackMessage
        ToastService.error(message, loadErrorMessage?.title);
      }
    };
    loadUserNotificationSettings();
  }, []);

  const visibleCategories = apiCategories.length ? apiCategories : CATEGORY_UPDATE_ORDER;

  useEffect(() => {
    if (!visibleCategories.includes(activeCategory)) {
      setActiveCategory(visibleCategories[0]);
    }
  }, [visibleCategories, activeCategory]);

  const tabs: Tab[] = visibleCategories.map(category => ({
    id: CATEGORY_TAB_ID[category],
    label: tabLabels[category],
    content: null,
  }));

  const toggle = (key: ChannelKey) => {
    const nextValue = !allSettings[activeCategory][key];
    const categoriesToUpdate = activeCategory === 'All'
      ? CATEGORY_UPDATE_ORDER.filter(category => categoryChannels[category].includes(key))
      : [activeCategory];

    const updatedAllSettings = {
      ...allSettings,
      ...categoriesToUpdate.reduce((acc, category) => {
        acc[category] = { ...allSettings[category], [key]: nextValue };
        return acc;
      }, {} as Partial<Record<SettingsCategory, DeliverySettings>>),
    } as Record<SettingsCategory, DeliverySettings>;

    setAllSettings(updatedAllSettings);
    setShowHideSetting(buildShowHideSettings(updatedAllSettings));

    categoriesToUpdate.forEach(category => { void saveUserNotificationSetting(category, key, nextValue); });
  };

  const currentSettings = allSettings[activeCategory];
  const visibleChannels = categoryChannels[activeCategory];

  return (
    <div className="notification-settings">
      <div className="notification-settings__header">
        <button className="notification-settings__back-btn" onClick={onBack} aria-label={backButtonLabel || 'Back to notifications'} type="button"><BackArrowIcon /></button>
        <h3 className="notification-settings__title">{panelTitle}</h3>
        <span className="notification-settings__gear-icon" aria-hidden="true"><GearIcon /></span>
      </div>

      <Tabs tabs={tabs} variant="inner" activeTab={CATEGORY_TAB_ID[activeCategory]} onTabChange={tab => setActiveCategory(TAB_ID_CATEGORY_MAP[tab.id] ?? 'All')} />

      <div className="notification-settings__body">
        <h4 className="notification-settings__section-title">{sectionTitle}</h4>
        <p className="notification-settings__section-desc">{sectionDescription}</p>

        {visibleChannels.map(key => {
          const cfg = channelConfig[key];
          return (
            <div key={key} className="notification-settings__channel-item">
              <span className="notification-settings__channel-icon" aria-hidden="true">{cfg.icon === 'email' ? <EmailIcon /> : <MonitorIcon />}</span>
              <div className="notification-settings__channel-info">
                <span className={`notification-settings__channel-name${cfg.bold ? ' notification-settings__channel-name--bold' : ''}`}>{cfg.label}</span>
                <span className="notification-settings__channel-desc">{cfg.desc}</span>
              </div>
              <Toggle checked={currentSettings[key]} onChange={() => toggle(key)} label={`Toggle ${cfg.label}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NotificationSettings;
